import * as opt from "lib/opt"
import * as objMethods from "lib/mods/objectMethods"

export function SetupObjectPrototypeMethods() {
	function isReactOrDOMElement(obj: any): bool {
		return (
			obj &&
			(obj._owner !== undefined ||
				obj.props !== undefined ||
				(typeof obj.tagName === "string" && obj.nodeType !== undefined) ||
				obj.anchorOrigin !== undefined ||
				Object.getPrototypeOf(obj) !== Object.prototype)
		)
	}

	function defineObjectProperty<T extends Record<string, any>, U>(
		name: string,
		fn: (this: T, ...args: any[]) => U
	) {
		Object.defineProperty(Object.prototype, name, {
			enumerable: false,
			writable: true,
			value: fn,
		})
	}

	defineObjectProperty("Merge", function <
		T extends Record<string, any>,
		U extends Record<string, any>,
	>(this: T, other: U): T & U {
		if (!this || isReactOrDOMElement(this)) {
			// If `this` is nil or a special React/DOM element, just return a copy
			return { ...this } as T & U
		}
		// Shallow merge of `this` and `other`
		return { ...this, ...other } as T & U
	})

	defineObjectProperty("Get", function <
		T extends Record<string, any>,
		K extends keyof T,
	>(this: T, prop: K): Opt<T[K]> {
		const value = this[prop]
		if (isNil(value)) {
			return opt.None()
		}
		return opt.Some(value)
	})

	defineObjectProperty("IsNil", function <T extends Record<string, any>>(this: T): this is nil {
		return isNil(this)
	})

	defineObjectProperty("IsEmpty", function <T extends Record<string, any>>(this: T): bool {
		return Object.keys(this).length === 0
	})

	Object.defineProperty(Object.prototype, "MapValues", {
		enumerable: false,
		writable: true,
		value: function <T extends Record<string, any>, U>(
			this: T,
			fn: (value: T[keyof T]) => U
		): { [K in keyof T]: U } {
			if (!this || isReactOrDOMElement(this)) {
				return {} as { [K in keyof T]: U }
			}
			return objMethods.MapValues(this, fn)
		},
	})

	Object.defineProperty(Object.prototype, "OmitBy", {
		enumerable: false,
		writable: true,
		value: function <T extends Record<string, any>>(
			this: T,
			predicate: (value: T[keyof T], key: keyof T) => bool
		): Partial<T> {
			if (!this || isReactOrDOMElement(this)) {
				return {} as Partial<T>
			}
			return objMethods.OmitBy(this, predicate)
		},
	})

	Object.defineProperty(Object.prototype, "Pick", {
		enumerable: false,
		writable: true,
		value: function <T extends Record<string, any>, K extends keyof T>(
			this: T,
			...keys: K[]
		): Pick<T, K> {
			if (!this || isReactOrDOMElement(this)) {
				return {} as Pick<T, K>
			}
			return objMethods.Pick(this, ...keys)
		},
	})

	Object.defineProperty(Object.prototype, "Omit", {
		enumerable: false,
		writable: true,
		value: function <T extends Record<string, any>, K extends keyof T>(
			this: T,
			...keys: K[]
		): Omit<T, K> {
			if (!this || isReactOrDOMElement(this)) {
				return {} as Omit<T, K>
			}
			return objMethods.Omit(this, ...keys)
		},
	})

	Object.defineProperty(Object.prototype, "Copy", {
		enumerable: false,
		writable: true,
		value: function <T extends Record<string, any>>(this: T): T {
			if (!this || isReactOrDOMElement(this)) {
				return {} as T
			}
			return { ...this }
		},
	})

	defineObjectProperty("Values", function <T extends Record<string, any>>(this: T): T[keyof T][] {
		if (!this || isReactOrDOMElement(this)) {
			return []
		}
		return Object.values(this)
	})

	defineObjectProperty("Keys", function <T extends Record<string, any>>(this: T): (keyof T)[] {
		return Object.keys(this)
	})

	defineObjectProperty("Entries", function <T extends Record<string, any>>(this: T): [
		keyof T,
		T[keyof T],
	][] {
		return Object.entries(this)
	})

	defineObjectProperty("ShallowEq", function <
		T extends Record<string, any>,
	>(this: T, other: T): bool {
		return this === other
	})

	defineObjectProperty("getValue", function <
		T extends Record<string, any>,
		K extends keyof T,
	>(this: T, prop: K): Opt<T[K]> {
		const value = this[prop]
		if (value === undefined) {
			return opt.None<T[K]>()
		}
		return opt.Some<T[K]>(value)
	})

	defineObjectProperty("Set", function <
		T extends Record<string, any>,
		K extends keyof T,
		V extends T[K],
	>(this: T, key: K, value: V) {
		this[key] = value
	})
}

const gt = globalThis as any

// Keep isNil for consistency
if (!gt.IsNil) {
	gt.IsNil = (value: unknown): value is nil => value == null || value === gt.nil
}

// Only need isNil descriptor for compatibility
const isNilDescriptor: PropertyDescriptor = {
	enumerable: false,
	writable: true,
	configurable: true,
	value: function () {
		return gt.IsNil(this)
	},
}

/* eslint-disable */
//@ts-ignore
;[
	Object.prototype,
	String.prototype,
	Number.prototype,
	Boolean.prototype,
	Array.prototype,
	Function.prototype,
].forEach((proto) => {
	try {
		Object.defineProperty(proto, "isNil", isNilDescriptor)
	} catch (e) {
		log.Warnf("Failed to define isNil on prototype: %j, %j", proto, e)
	}
})
